import { useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { Button, Collapse, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Popover } from '@mui/material';

import { ACTION_MENU_BUTTON_TYPE, roundedComponentVariants, componentsSize } from 'constants/components';
import ColorButton from 'components/simple/ColorButton';
import { menuStyles } from 'components/simple/MenuPanel';
import { ArrowDownIcon, ArrowUpIcon, ButtonMoreIcon } from 'assets/icons';

const styles = {
  menuSubItem: ({ breakpoints, palette }) => ({
    borderTop: `1px solid ${palette.colors.darkPale}`,
    '.MuiListItemButton-root': {
      paddingLeft: 2,
      [breakpoints.up('md')]: {
        paddingLeft: 2.5,
      },
    },
  }),
  menuPaper: [
    menuStyles.panel,
    {
      marginBlock: 1,
    },
  ],
};

const ActionsMenu = ({
  id = 'actions',
  buttonProps: { title, type, startIcon, endIcon, size, colored, ...props } = {
    type: ACTION_MENU_BUTTON_TYPE.ICON,
    title: '',
    startIcon: null,
    endIcon: null,
    size: componentsSize.medium,
    colored: false,
  },
  options,
  customStyles = null,
  getIsSelectedOption = null,
  customPopoverProps = null,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [submenuIndex, setSubmenuIndex] = useState(null);

  const isOpenMenu = Boolean(menuAnchorEl);

  const optionsToShow = options.filter(({ hideOption }) => !hideOption);

  const handleOpenMenu = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(null);
    setSubmenuIndex(null);
  };

  const handleItemClick = (event, option, index) => {
    if (!isEmpty(option.subItems)) {
      setSubmenuIndex((prevState) => (prevState === index ? null : index));

      return;
    }

    if (typeof option.onClick === 'function') {
      option.onClick();
      handleCloseMenu(event);
    }
  };

  if (!optionsToShow.length) {
    return null;
  }

  const button = {
    [ACTION_MENU_BUTTON_TYPE.ICON]: (
      <ColorButton
        transparent={!colored}
        variant={roundedComponentVariants.rounded}
        icon={ButtonMoreIcon}
        size={size}
        id={`${id}-menu-button`}
        onClick={handleOpenMenu}
        color={colored ? 'secondary' : 'primary'}
        aria-controls={isOpenMenu ? `${id}-menu` : undefined}
        aria-expanded={isOpenMenu ? 'true' : undefined}
        aria-haspopup="true"
        {...props}
      />
    ),
    [ACTION_MENU_BUTTON_TYPE.REGULAR]: (
      <Button
        color="info"
        size="small"
        startIcon={startIcon}
        endIcon={endIcon}
        onClick={handleOpenMenu}
        id={`${id}-button`}
        aria-haspopup="true"
        aria-controls={isOpenMenu ? `${id}-menu` : undefined}
        aria-expanded={isOpenMenu ? 'true' : undefined}
        {...props}
      >
        {title}
      </Button>
    ),
  }[type];

  return (
    <>
      {button}

      <Popover
        disableAutoFocus
        id={`${id}-menu`}
        anchorEl={menuAnchorEl}
        open={isOpenMenu}
        onClose={handleCloseMenu}
        aria-labelledby={`${id}-menu-button`}
        slotProps={{
          paper: { sx: styles.menuPaper },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        {...customPopoverProps}
      >
        <List disablePadding>
          {optionsToShow.map((option, index) => {
            const isExpandable = !isEmpty(option.subItems);
            const isExpanded = index === submenuIndex;
            const expandIcon = isExpanded ? <ArrowUpIcon /> : <ArrowDownIcon />;
            const withIcon = isExpandable || option?.icon;
            const isSelected = !!getIsSelectedOption && getIsSelectedOption(option);

            return (
              <ListItem divider disablePadding key={option.label} sx={[menuStyles.item, customStyles?.option]}>
                <ListItemButton
                  disableRipple
                  selected={isSelected}
                  onClick={(event) => handleItemClick(event, option, index)}
                >
                  {withIcon && <ListItemIcon>{isExpandable ? expandIcon : option.icon}</ListItemIcon>}

                  <ListItemText primary={option.label} />
                </ListItemButton>

                {isExpandable && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List disablePadding>
                      {option.subItems.map((subItem) => (
                        <ListItem disablePadding key={subItem.label} sx={[menuStyles.item, styles.menuSubItem]}>
                          <ListItemButton disableRipple onClick={(event) => handleItemClick(event, subItem)}>
                            {subItem?.icon && <ListItemIcon>{subItem.icon}</ListItemIcon>}
                            <ListItemText primary={subItem.label} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                )}
              </ListItem>
            );
          })}
        </List>
      </Popover>
    </>
  );
};

const itemPropTypes = {
  value: PropTypes.string,
  label: PropTypes.node.isRequired,
  icon: PropTypes.node,
  onClick: PropTypes.func,
  hideOption: PropTypes.bool,
};

ActionsMenu.propTypes = {
  id: PropTypes.string,
  buttonProps: PropTypes.shape({
    type: PropTypes.oneOf(Object.values(ACTION_MENU_BUTTON_TYPE)),
    title: PropTypes.string,
    startIcon: PropTypes.node,
    endIcon: PropTypes.node,
    size: PropTypes.oneOf(Object.values(componentsSize)),
    colored: PropTypes.bool,
  }),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      ...itemPropTypes,
      subItems: PropTypes.arrayOf(PropTypes.shape(itemPropTypes)),
    }),
  ).isRequired,
  customStyles: PropTypes.shape({
    option: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.func]),
  }),
  getIsSelectedOption: PropTypes.func,
  customPopoverProps: PropTypes.shape({}),
};
export default ActionsMenu;
